import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Snackbar,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Science,
  Schedule,
  PriorityHigh,
  Biotech,
  CheckCircle,
  LocalFireDepartment,
  Warning,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Search,
  Clear,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';

const AUPreparationChimiqueDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // State management
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Batch functionality state
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [batchPreparationDialog, setBatchPreparationDialog] = useState(false);
  const [preparingBatch, setPreparingBatch] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [batchSampleData, setBatchSampleData] = useState<Record<string, any>>({});
  
  // Batch common values (identical to SAA)
  const [batchCommonValues, setBatchCommonValues] = useState({
    digestionMethod: '',
    initialVolume: 0,
    applyToAll: {
      digestionMethod: true,
      initialVolume: true,
    }
  });
  
  // Individual sample state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [preparationDialog, setPreparationDialog] = useState(false);
  
  // Dashboard stats
  const [stats, setStats] = useState({
    pending_samples: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
  });
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(250);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalBatches, setTotalBatches] = useState(0);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when search changes
  useEffect(() => {
    loadAUPreparationData();
  }, [debouncedSearch]);

  const loadAUPreparationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🔥 CRITICAL FIX: Fetch total count first, then fetch ALL samples
      // This prevents pagination cutoff for older AU_PREP samples when sorted by date DESC
      // Pattern copied from ICPAnalystDashboard (proven working solution)
      // 🔥 CRITICAL FIX: Include ALL lab statuses for 3-lab workflows (SAA + ICP + AU + Physico)
      const statuses = [
        SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,
        SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,  // Multi-lab samples doing ICP prep in parallel
        SampleStatus.LABORATOIRE_ICP_ANALYSE,         // Multi-lab samples after ICP prep, need AU prep
        SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE,   // Multi-lab samples doing SAA prep in parallel
        SampleStatus.LABORATOIRE_SAA_ANALYSE,         // Multi-lab samples after SAA prep, need AU prep
        SampleStatus.VALIDATION_SERVICE,              // Multi-lab samples in ICP/SAA validation, still need AU prep
        SampleStatus.PREPARATION_MECANIQUE,           // Multi-lab samples waiting for prep
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE,   // 🔧 FIX: Added Physico base status for multi-lab samples
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE, // 🔧 FIX: Added Physico analysis status for multi-lab samples
      ];
      
      // Step 1: Get total count
      const countResponse = await samplesService.getSamples(1, 1, {
        status: statuses,
      });
      const totalSamples = countResponse.total || 20000;
      
      // Step 2: Fetch ALL samples using total as limit
      const response = await samplesService.getSamples(1, totalSamples, {
        status: statuses,
      });
      
      const auSamples = (Array.isArray(response) ? response : response.samples || []).filter((sample: any) => {
        // Regular AU samples
        if (sample.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE') {
          return true;
        }
        
        // Multi-lab samples (BGM or custom routing) - check if ANY selected element is routed to AU
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        if (isMultiLabSample) {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          
          // 🔧 FIX: Check if ANY selected element routes to AU (not just elementRouting values)
          const hasAuElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_AU'
          );
          
          // 🔧 FIX: Also check laboratories_involved as fallback (for method-based routing)
          const hasAuInLabs = sample.laboratories_involved?.includes('LABORATOIRE_AU') || false;
          
          if ((hasAuElements || hasAuInLabs) && !sample.metadonnees?.au_preparation_completed) {
            return true; // Show ALL multi-lab samples that need AU prep, regardless of status
          }
        }
        
        return false;
      });
      
      // 🔍 Apply search filter (frontend-side)
      let filteredSamples = auSamples;
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        filteredSamples = auSamples.filter((sample: any) => 
          sample.sample_id?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_info?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.reference_client?.toLowerCase().includes(searchLower)
        );
      }
      
      setSamples(filteredSamples as Sample[]);
      
      // Calculate stats (from filtered samples)
      const pending = filteredSamples.filter((s: any) => {
        if (s.statut === 'LABORATOIRE_AU_PREP_CHIMIQUE') return true;
        const isMultiLab = s.metadonnees?.multi_lab_workflow === true || s.metadonnees?.bgm_method_selected === true;
        // Multi-lab samples in any ICP workflow stage that still need AU prep
        return isMultiLab && ['LABORATOIRE_ICP_PREP_CHIMIQUE', 'LABORATOIRE_ICP_ANALYSE', 'VALIDATION_SERVICE'].includes(s.statut);
      }).length;
      const urgent = filteredSamples.filter((s: any) => s.priorite === 'URGENTE').length;
      
      setStats({
        pending_samples: pending,
        in_progress: 0,
        completed_today: 0,
        urgent_samples: urgent,
      });
      
    } catch (error) {
      console.error('Error loading AU preparation data:', error);
      setError('Erreur lors du chargement des données AU');
    } finally {
      setLoading(false);
    }
  };

  // Batch handling functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individual: Sample[] = [];

    samples.forEach(sample => {
      const batchNumber = getBatchNumber(sample);
      if (batchNumber) {
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individual.push(sample);
      }
    });

    return { batches, individual };
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchNumber)) {
        newSet.delete(batchNumber);
      } else {
        newSet.add(batchNumber);
      }
      return newSet;
    });
  };

  // Initialize batch sample data (identical to SAA)
  const initializeBatchSampleData = (samples: Sample[]) => {
    const initialData: Record<string, any> = {};
    samples.forEach(sample => {
      // 🔬 SCALE INTEGRATION: Use scale weight if available, otherwise default 0.5
      const scaleWeight = sample.metadonnees?.scale_weights?.au?.weight_grams;
      const hasScaleWeight = scaleWeight !== undefined && scaleWeight !== null;
      
      initialData[sample.id] = {
        initialWeight: hasScaleWeight ? scaleWeight : 0.5, // Use scale weight or default
        hasScaleWeight: hasScaleWeight, // Flag to show indicator in UI
        scaleRecordedAt: sample.metadonnees?.scale_weights?.au?.recorded_at,
        digestionMethod: batchCommonValues.digestionMethod || '',
        initialVolume: batchCommonValues.initialVolume || 0,
        dilutions: [],
        volumeTaken: 0,
        finalVolume: 0,
        calculatedDilutionFactor: 1,
        // Additional dilution fields for choice 2
        vf1: 0,
        vp1: 0,
      };
    });
    setBatchSampleData(initialData);
  };

  // Update individual sample data in batch with auto-calculation (identical to SAA)
  const updateBatchSampleData = (sampleId: string, field: string, value: any) => {
    setBatchSampleData(prev => {
      const currentData = prev[sampleId] || {
        initialWeight: 0,
        digestionMethod: '',
        initialVolume: 0,
        dilutions: [],
        volumeTaken: 0,
        finalVolume: 0,
        calculatedDilutionFactor: 1,
        vf1: 0,
        vp1: 0,
      };
      const updatedData = {
        ...currentData,
        [field]: value
      };
      
      // Auto-calculate dilution factor when volume fields change
      if (field === 'volumeTaken' || field === 'finalVolume') {
        const volumeTaken = field === 'volumeTaken' ? value : currentData.volumeTaken || 0;
        const finalVolume = field === 'finalVolume' ? value : currentData.finalVolume || 0;
        
        if (volumeTaken > 0 && finalVolume > 0) {
          updatedData.calculatedDilutionFactor = finalVolume / volumeTaken;
        } else {
          updatedData.calculatedDilutionFactor = 1;
        }
      }
      
      return {
        ...prev,
        [sampleId]: updatedData
      };
    });
  };

  // Duplicate value to all samples in batch (identical to SAA)
  const duplicateValueToAll = (field: string, value: any, sourceSampleId: string) => {
    if (!selectedBatch?.samples) return;
    
    const updatedData = { ...batchSampleData };
    
    selectedBatch.samples.forEach(sample => {
      if (sample.id !== sourceSampleId) {
        if (!updatedData[sample.id]) {
          updatedData[sample.id] = {
            initialWeight: 0,
            digestionMethod: '',
            initialVolume: 0,
            dilutions: [],
            volumeTaken: 0,
            finalVolume: 0,
            calculatedDilutionFactor: 1,
            vf1: 0,
            vp1: 0,
          };
        }
        updatedData[sample.id][field] = value;
        
        // Recalculate dilution factor if volumes changed
        if (field === 'volumeTaken' || field === 'finalVolume') {
          const sampleData = updatedData[sample.id];
          const volumeTaken = field === 'volumeTaken' ? value : (sampleData.volumeTaken || 0);
          const finalVolume = field === 'finalVolume' ? value : (sampleData.finalVolume || 0);
          
          if (volumeTaken > 0 && finalVolume > 0) {
            updatedData[sample.id].calculatedDilutionFactor = finalVolume / volumeTaken;
          } else {
            updatedData[sample.id].calculatedDilutionFactor = 1;
          }
        }
      }
    });
    
    setBatchSampleData(updatedData);
  };

  // Save batch preparation and move all samples to AU analysis
  const handleBatchPreparationSave = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      // Validate all samples have required data (identical to SAA)
      const invalidSamples = selectedBatch.samples.filter(sample => {
        const sampleData = batchSampleData[sample.id];
        return !sampleData?.initialWeight || !sampleData?.digestionMethod || !sampleData?.initialVolume;
      });

      if (invalidSamples.length > 0) {
        throw new Error(`${invalidSamples.length} échantillon(s) incomplet(s) - tous les champs sont requis`);
      }

      
      // Prepare bulk update data (identical to SAA)
      const bulkUpdateData = selectedBatch.samples.map(sample => {
        const sampleData = batchSampleData[sample.id];
        
        const preparationData = {
          sampleId: sample.sample_id,
          initialWeight: sampleData.initialWeight,
          digestionMethod: sampleData.digestionMethod,
          initialVolume: sampleData.initialVolume,
          dilutions: sampleData.dilutions || [],
          totalDilutionFactor: sampleData.calculatedDilutionFactor || 1,
          // Dilution fields for choice 1
          finalVolume: sampleData.finalVolume || 0,
          volumeTaken: sampleData.volumeTaken || 0,
          // Additional dilution fields for choice 2
          vf1: sampleData.vf1 || 0,
          vp1: sampleData.vp1 || 0,
          timestamp: new Date().toISOString(),
          signature: `${user?.firstName} ${user?.lastName}`,
          technician_id: user?.id,
          batch_preparation: true,
          batch_number: selectedBatch.batchNumber,
        };

        // For multi-lab samples (BGM or custom), check if both preparations will be complete
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        const icpPrepComplete = sample.metadonnees?.icp_preparation_completed === true;
        const icpAnalysisComplete = sample.metadonnees?.icp_analysis_completed === true;
        let newStatus;
        
        // 🔥 CRITICAL FIX: Don't overwrite status if sample is already in validation/analysis workflow
        // This prevents AU prep from removing samples from ICP Supervisor's queue
        if (isMultiLabSample) {
          // Check if sample is already in a validation or analysis stage for another lab
          const currentStatus = sample.statut;
          const protectedStatuses = [
            'VALIDATION_SERVICE',           // Already in validation - DON'T CHANGE
            'LABORATOIRE_ICP_ANALYSE',      // In ICP analysis - DON'T CHANGE
            'GENERATION_CERTIFICAT',        // Already generating certificate
          ];
          
          if (protectedStatuses.includes(currentStatus)) {
            // Keep current status - just update metadata
            newStatus = currentStatus as SampleStatus;
            console.log(`🔒 AU Prep: Keeping status ${currentStatus} for multi-lab sample ${sample.sample_id} (ICP workflow in progress)`);
          } else {
            // Sample is in AU prep workflow, move to AU analysis
            newStatus = SampleStatus.LABORATOIRE_AU_ANALYSE;
          }
        } else {
          // Regular AU sample - always move to AU analysis
          newStatus = SampleStatus.LABORATOIRE_AU_ANALYSE;
        }

        return {
          id: sample.id,
          metadonnees: {
            ...sample.metadonnees,
            au_preparation: preparationData,
            au_preparation_completed: true,
            digestion_started: true,
            digestion_method: sampleData.digestionMethod,
            preparation_completed_at: new Date().toISOString(),
            // Keep existing element_lab_routing (don't override per-sample routing)
            element_lab_routing: sample.metadonnees?.element_lab_routing,
          },
          status: newStatus,
          status_comment: isMultiLabSample 
            ? (icpPrepComplete 
                ? `Préparation AU terminée (multi-lab) - Toutes préparations complètes, prêt pour analyse` 
                : `Préparation AU terminée (multi-lab) - Méthode: ${sampleData.digestionMethod}, En attente préparation ICP`)
            : `Préparation AU terminée - Méthode: ${sampleData.digestionMethod}, Poids initial: ${sampleData.initialWeight}g, Volume initial: ${sampleData.initialVolume}mL, Facteur dilution: ${sampleData.calculatedDilutionFactor || 1}`
        };
      });

      // 🚀 BATCH OPTIMIZATION: Build sample-specific updates for batch API
      console.log(`🔄 Batch updating ${bulkUpdateData.length} AU preparation samples...`);
      
      const sampleIds = bulkUpdateData.map(item => item.id);
      const batchUpdates = {};
      
      // Build sample-specific metadata updates
      bulkUpdateData.forEach(sampleData => {
        const preparationData = sampleData.metadonnees.au_preparation;
        console.log(`📊 Sample ${selectedBatch.samples.find(s => s.id === sampleData.id)?.sample_id} preparation data:`, preparationData);
        
        batchUpdates[sampleData.id] = sampleData.metadonnees;
      });

      // Determine if we need status updates (check if any sample status changed)
      const needsStatusUpdate = bulkUpdateData.some(sampleData => {
        const originalSample = selectedBatch.samples.find(s => s.id === sampleData.id);
        return originalSample && sampleData.status !== originalSample.statut;
      });

      let statusUpdate = null;
      if (needsStatusUpdate) {
        // For AU preparation, samples may have different target statuses (BGM vs regular)
        // So we'll handle status updates individually for mixed batches
        const hasMixedStatuses = new Set(bulkUpdateData.map(item => item.status)).size > 1;
        
        if (!hasMixedStatuses) {
          // All samples have same target status - use batch update
          const targetStatus = bulkUpdateData[0].status;
          const isMultiLabBatch = bulkUpdateData.some(item => {
            const sample = selectedBatch.samples.find(s => s.id === item.id);
            return sample?.metadonnees?.multi_lab_workflow === true || sample?.metadonnees?.bgm_method_selected === true;
          });
          
          statusUpdate = {
            status: targetStatus,
            comment: isMultiLabBatch 
              ? `Préparation AU terminée (multi-lab) - Lot: ${selectedBatch.batchNumber}`
              : `Préparation AU terminée - Lot: ${selectedBatch.batchNumber}`
          };
        }
      }

      console.log(`📦 Batch updates object:`, batchUpdates);
      console.log(`📋 Status update:`, statusUpdate);

      let result;
      if (statusUpdate) {
        // Use batch API for metadata and status
        result = await samplesService.updateSampleBatch(sampleIds, batchUpdates, statusUpdate);
      } else if (needsStatusUpdate) {
        // Mixed statuses - update metadata in batch, then handle statuses individually
        const metadataResult = await samplesService.updateSampleBatch(sampleIds, batchUpdates);
        
        // Update statuses individually for mixed batches
        const statusPromises = bulkUpdateData
          .filter(sampleData => {
            const originalSample = selectedBatch.samples.find(s => s.id === sampleData.id);
            return originalSample && sampleData.status !== originalSample.statut;
          })
          .map(async (sampleData) => {
            try {
              await samplesService.updateSampleStatus(sampleData.id, sampleData.status, sampleData.status_comment);
              return { success: true, id: sampleData.id };
            } catch (error) {
              return { success: false, id: sampleData.id, error: error.message };
            }
          });

        const statusResults = await Promise.all(statusPromises);
        const statusErrors = statusResults.filter(r => !r.success).map(r => `${r.id}: ${r.error}`);
        
        result = {
          updated: metadataResult.updated,
          errors: [...metadataResult.errors, ...statusErrors]
        };
      } else {
        // Only metadata updates needed
        result = await samplesService.updateSampleBatch(sampleIds, batchUpdates);
      }

      console.log(`✅ Batch update completed: ${result.updated} samples updated, ${result.errors.length} errors`);

      if (result.errors.length > 0) {
        console.warn(`⚠️ Some errors during bulk save:`, result.errors);
      }

      // Show success notification
      setSnackbarMessage(`✅ Lot ${selectedBatch.batchNumber} préparé avec succès! ${result.updated} échantillons déplacés vers AU Analyse.`);
      setSnackbarOpen(true);
      
      // Small delay to let user see the success message before refresh
      setTimeout(() => {
        setBatchPreparationDialog(false);
        setSelectedBatch(null);
        setBatchSampleData({});
        loadAUPreparationData();
      }, 1500);
      
    } catch (error: any) {
      console.error('Error saving batch preparation:', error);
      setError(`Erreur lors de la sauvegarde du lot: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données AU...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadAUPreparationData} sx={{ ml: 2 }}>
          Réessayer
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalFireDepartment sx={{ fontSize: '2rem', color: '#e65100' }} />
          🔥 Fire Assay & Extraction Métaux Précieux - {user?.firstName} {user?.lastName}
          </Typography>

      {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.pending_samples}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  En attente Fire Assay
                  </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffebee', border: '1px solid #ef5350' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                    {stats.urgent_samples}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  Urgents
                  </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      {/* 🔍 SEARCH FIELD */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Rechercher par N° Lot ou N° Échantillon..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ 
            width: 400,
            backgroundColor: 'white',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {debouncedSearch && (
          <Chip 
            label={`Recherche: "${debouncedSearch}"`}
            onDelete={() => setSearchTerm('')}
            color="primary"
            variant="outlined"
          />
        )}
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadAUPreparationData}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {/* Samples List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science />
            📋 Échantillons en Préparation Fire Assay ({samples.length})
          </Typography>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Science sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun échantillon en préparation Fire Assay
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les échantillons apparaîtront ici quand ils seront assignés au laboratoire AU.
              </Typography>
            </Box>
          ) : (
            <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Méthodes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const { batches, individual } = groupSamplesByBatch(samples);
                    const items: Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}> = [];
                    
                    Object.entries(batches).forEach(([batchNumber, batchSamples]) => {
                      items.push({ type: 'batch', batchNumber, samples: batchSamples });
                    });
                    individual.forEach(sample => {
                      items.push({ type: 'individual', sample });
                    });

                    // 🚀 Apply frontend pagination
                    const paginatedItems = items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
                    
                    // Store total for pagination controls
                    if (items.length !== totalBatches) {
                      setTimeout(() => setTotalBatches(items.length), 0);
                    }

                    return paginatedItems.map((item, index) => {
                      if (item.type === 'batch') {
                        const isExpanded = expandedBatches.has(item.batchNumber);
                        const totalWeight = item.samples.reduce((sum, s) => sum + (s.metadonnees?.poids_kg || 0), 0);
                        
                        return (
                          <React.Fragment key={item.batchNumber}>
                            <TableRow sx={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #e65100' }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => toggleBatchExpansion(item.batchNumber)}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Box>
                                    <Box sx={{ 
                                      backgroundColor: '#e65100', 
                                      color: 'white', 
                                      px: 1, 
                                      py: 0.5, 
                                      borderRadius: 1,
                                      fontWeight: 'bold'
                                    }}>
                                      LOT AU
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                                      {item.batchNumber}
                                    </Typography>
                                    <Chip 
                                      label={`${item.samples.length} échantillons`} 
                                      size="small" 
                                      color="warning" 
                                      variant="outlined"
                                    />
                                    <Chip 
                                      label={`${totalWeight.toFixed(2)} kg total`} 
                                      size="small" 
                                      color="info" 
                                      variant="outlined"
                                    />
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>{item.samples[0]?.client?.company_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'URGENTE' : 'NORMALE'}
                                  size="small"
                                  color={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell>Fire Assay AU</TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="warning"
                                  startIcon={preparingBatch ? <CircularProgress size={16} /> : <LocalFireDepartment />}
                                  disabled={preparingBatch}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setPreparingBatch(true);
                                    await new Promise(resolve => setTimeout(resolve, 50));
                                    setSelectedBatch({ batchNumber: item.batchNumber, samples: item.samples });
                                    initializeBatchSampleData(item.samples);
                                    setBatchPreparationDialog(true);
                                    setPreparingBatch(false);
                                  }}
                                  sx={{ ml: 'auto' }}
                                >
                                  Préparer le Lot
                                </Button>
                              </TableCell>
                            </TableRow>

                            {/* Individual Sample Rows - Only show when expanded */}
                            {isExpanded && item.samples.map((sample, sampleIndex) => (
                              <TableRow 
                                key={sample.id}
                    sx={{ 
                                  backgroundColor: '#fff8f0',
                                  borderLeft: '2px solid #e65100'
                                }}
                              >
                                <TableCell sx={{ pl: 4 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight="bold">
                            {sample.sample_id}
                          </Typography>
                                    <Chip 
                                      label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>
                          <Chip
                            label={sample.priorite}
                            size="small"
                                    color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('AU')).join(', ') || 'AU Standard'}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Science />}
                                    onClick={() => {
                                      setSelectedSample(sample);
                                      setPreparationDialog(true);
                                    }}
                                  >
                                    Préparer Individuel
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      } else {
                        // Individual sample
                        const sample = item.sample;
                        return (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {sample.sample_id}
                          </Typography>
                            </TableCell>
                            <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={sample.priorite}
                                size="small"
                                color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('AU')).join(', ') || 'AU Standard'}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Science />}
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setPreparationDialog(true);
                                }}
                              >
                                Préparer
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* 🚀 PAGINATION CONTROLS */}
            <TablePagination
              component="div"
              count={totalBatches}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 250]}
              labelRowsPerPage="Lots par page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`} lots`
              }
              sx={{
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
              }}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Batch Preparation Dialog - COMPLETE SAA-STYLE */}
      <Dialog 
        open={batchPreparationDialog} 
        onClose={() => setBatchPreparationDialog(false)} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#e65100',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Science sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                🧪 Préparation AU - Lot {selectedBatch?.batchNumber}
              </Typography>
              <Chip 
                label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="warning" 
                variant="outlined" 
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            {/* Progress Indicator */}
          <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                Prêts: {selectedBatch?.samples?.filter(sample => {
                  const sampleData = batchSampleData[sample.id];
                  return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                }).length || 0}/{selectedBatch?.samples?.length || 0}
            </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>
            {/* Batch Samples Table - Complete SAA Style */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    🧪 Saisie des Préparations Chimiques - Lot {selectedBatch?.batchNumber}
            </Typography>
            
                  {/* Digestion Methods Display */}
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {['HCl + HNO3', 'Eau régale', 'HNO3', 'HF + HClO4'].map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <TableContainer 
                  sx={{ 
                    maxHeight: 'calc(100vh - 300px)',
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ 
                        backgroundColor: '#fff3e0',
                        '& .MuiTableCell-head': {
                          backgroundColor: '#e65100',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          padding: '6px 8px',
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell>Échantillon</TableCell>
                        <TableCell>Poids initial (g)</TableCell>
                        <TableCell>Méthode digestion</TableCell>
                        <TableCell>Vol initial (mL)</TableCell>
                        <TableCell>Vol prélevé (mL)</TableCell>
                        <TableCell>Vol final (mL)</TableCell>
                        <TableCell>Facteur dilution</TableCell>
                        <TableCell>vf1 (mL)</TableCell>
                        <TableCell>vp1 (mL)</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBatch?.samples
                        .sort((a, b) => {
                          const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                          const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                          return a.sample_id.localeCompare(b.sample_id);
                        })
                        .map((sample, index) => {
                        const sampleData = batchSampleData[sample.id] || {
                          initialWeight: 0,
                          digestionMethod: '',
                          initialVolume: 0,
                          volumeTaken: 0,
                          finalVolume: 0,
                          calculatedDilutionFactor: 1,
                          vf1: 0,
                          vp1: 0,
                        };
                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ 
                              backgroundColor: index % 2 === 0 ? '#fff8f0' : 'white',
                              '&:hover': { backgroundColor: '#ffecc7' },
                              '& .MuiTableCell-root': {
                                padding: '4px 6px',
                                fontSize: '0.75rem'
                              }
                            }}
                          >
                            {/* Sample ID */}
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                {sample.sample_id}
                              </Typography>
                            </TableCell>

                            {/* Initial Weight */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialWeight || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialWeight', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.0001 }}
                                  sx={{ 
                                    width: 90,
                                    // 🔬 SCALE: Green border if weight came from scale
                                    '& .MuiOutlinedInput-root': sampleData.hasScaleWeight ? {
                                      '& fieldset': { borderColor: 'success.main', borderWidth: 2 }
                                    } : {}
                                  }}
                                  placeholder="0.5"
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                {/* 🔬 SCALE INDICATOR: Show badge if weight came from scale */}
                                {sampleData.hasScaleWeight && (
                                  <Chip
                                    label="⚖️"
                                    size="small"
                                    color="success"
                                    title={`Poids de la balance - ${new Date(sampleData.scaleRecordedAt).toLocaleString()}`}
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.6rem',
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialWeight', sampleData.initialWeight, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Digestion Method */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={sampleData.digestionMethod || ''}
                                    onChange={(e) => updateBatchSampleData(sample.id, 'digestionMethod', e.target.value)}
                                    sx={{ fontSize: '0.7rem', height: '28px' }}
                                  >
                                    <MenuItem value="HCl + HNO3">HCl + HNO3</MenuItem>
                                    <MenuItem value="Eau régale">Eau régale</MenuItem>
                                    <MenuItem value="HNO3">HNO3</MenuItem>
                                    <MenuItem value="HF + HClO4">HF + HClO4</MenuItem>
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('digestionMethod', sampleData.digestionMethod, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Initial Volume */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 85 }}
                                  placeholder="100"
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialVolume', sampleData.initialVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Final Volume */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.finalVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'finalVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 85 }}
                                  placeholder="100"
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('finalVolume', sampleData.finalVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Volume Taken */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.volumeTaken || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'volumeTaken', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 85 }}
                                  placeholder="10"
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('volumeTaken', sampleData.volumeTaken, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Calculated Dilution Factor */}
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  color: sampleData.calculatedDilutionFactor > 1 ? 'success.main' : 'text.secondary',
                                  minWidth: 60
                                }}
                              >
                                {sampleData.calculatedDilutionFactor?.toFixed(4) || '1.0000'}
                              </Typography>
                            </TableCell>

                            {/* vf1 Field */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.vf1 || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'vf1', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 85 }}
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('vf1', sampleData.vf1, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* vp1 Field */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.vp1 || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'vp1', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 85 }}
                                  InputProps={{
                                    sx: { fontSize: '0.75rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('vp1', sampleData.vp1, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'warning.main',
                                    '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>

                            {/* Status */}
                            <TableCell>
                              <Chip
                                label={
                                  sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume
                                    ? "✅ Prêt" 
                                    : "⏳ En cours"
                                }
                                size="small"
                                color={
                                  sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume
                                    ? "success" 
                                    : "warning"
                                }
                                sx={{ fontSize: '0.6rem', height: '18px' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Batch Summary */}
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: '#fff3e0', 
                  borderRadius: 1, 
                  border: '1px solid #ffb74d'
                }}>
                  <Typography variant="h6" color="warning.main" gutterBottom sx={{ fontSize: '0.9rem' }}>
                    📊 Résumé du Lot de Préparation
                  </Typography>
                  <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={2}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Poids Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialWeight || 0);
                        }, 0).toFixed(3)} g
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Prêts</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.filter(sample => {
                          const sampleData = batchSampleData[sample.id];
                          return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                        }).length || 0}
                </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Volume Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialVolume || 0);
                        }, 0)} mL
                </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: 'white', borderTop: '1px solid #ddd' }}>
          <Button 
            onClick={() => setBatchPreparationDialog(false)}
            color="inherit"
          >
            Annuler
          </Button>
          <Button 
            variant="contained"
            color="warning"
            startIcon={loading ? <CircularProgress size={16} /> : <Science />}
            disabled={loading || !selectedBatch?.samples.length}
            onClick={handleBatchPreparationSave}
          >
            {loading ? 'Traitement...' : `💾 Finaliser Lot Préparation (${selectedBatch?.samples.length || 0} échantillons)`}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AUPreparationChimiqueDashboard; 